// frontend/src/pages/admin/UsersList.js
import { useEffect, useState } from "react";
import { adminGetUsers } from "../../library/allauth";
import { UserPlus, Search } from "lucide-react";
import UserDetail from "./UserDetail";

// Map group name → display label
const ROLE_LABELS = {
  xroadz_admin: "Super Admin",
  customer_admin: "Customer Admin",
  customer_user: "Customer User",
  // legacy fallbacks
  admin: "Admin",
  user: "User",
};

const ROLE_BADGE_COLORS = {
  xroadz_admin: "bg-purple-100 text-purple-700",
  customer_admin: "bg-blue-100 text-blue-700",
  customer_user: "bg-gray-100 text-gray-600",
  admin: "bg-purple-100 text-purple-700",
  user: "bg-gray-100 text-gray-600",
};

const STATUS_STYLES = {
  active: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  disabled: "bg-gray-100 text-gray-500",
};
const STATUS_DOT = {
  active: "bg-green-500",
  pending: "bg-yellow-400",
  disabled: "bg-gray-400",
};

export default function UsersList({
  onRegister,
  defaultFilter = "all",
  currentUserRole,
  currentUsername,
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState(defaultFilter);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadUsers = () => {
    setLoading(true);
    adminGetUsers()
      .then((res) => {
        if (res.status === 200) setUsers(res.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Derive all regions across users (for UserDetail allRegions prop)
  const allRegions = [
    ...new Map(users.flatMap((u) => u.regions).map((r) => [r.id, r])).values(),
  ];

  if (selectedUser) {
    // Only show regions belonging to the selected user's customer
    const userCustomerRegions = selectedUser.customer_id
      ? [
          ...new Map(
            users
              .filter((u) => u.customer_id === selectedUser.customer_id)
              .flatMap((u) => u.regions)
              .map((r) => [r.id, r]),
          ).values(),
        ]
      : allRegions; // Super Admin (no customer) sees all

    return (
      <UserDetail
        user={selectedUser}
        allRegions={userCustomerRegions}
        onBack={() => setSelectedUser(null)}
        onUpdated={() => {
          loadUsers();
          setSelectedUser(null);
        }}
        onDeleted={() => {
          loadUsers();
          setSelectedUser(null);
        }}
        currentUserRole={currentUserRole}
        currentUsername={currentUsername}
      />
    );
  }

  const counts = {
    total: users.length,
    active: users.filter((u) => u.status === "active").length,
    pending: users.filter((u) => u.status === "pending").length,
    disabled: users.filter((u) => u.status === "disabled").length,
  };

  const filtered = users.filter((u) => {
    const matchSearch =
      u.username.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || u.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const isSuperAdmin = currentUserRole === "xroadz_admin";

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage portal access and permissions
          </p>
        </div>
        <button
          onClick={onRegister}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white text-sm font-medium rounded-lg hover:bg-emerald-800 transition-colors"
        >
          <UserPlus size={16} /> Register New User
        </button>
      </div>

      {/* Stat Chips */}
      <div className="flex gap-3 mb-6">
        {[
          { key: "all", label: "Total", count: counts.total },
          { key: "active", label: "Active", count: counts.active },
          { key: "pending", label: "Pending", count: counts.pending },
          { key: "disabled", label: "Disabled", count: counts.disabled },
        ].map((chip) => (
          <button
            key={chip.key}
            onClick={() => setFilterStatus(chip.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
              filterStatus === chip.key
                ? "bg-emerald-700 text-white border-emerald-700"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {chip.label}
            <span
              className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                filterStatus === chip.key
                  ? "bg-emerald-600 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {chip.count}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            Loading users...
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-3 font-medium text-gray-500">
                  Name
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">
                  Email
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">
                  Role
                </th>
                {/* Only Super Admin sees the Customer column */}
                {isSuperAdmin && (
                  <th className="text-left px-4 py-3 font-medium text-gray-500">
                    Customer
                  </th>
                )}
                <th className="text-left px-4 py-3 font-medium text-gray-500">
                  Regions
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={isSuperAdmin ? 6 : 5}
                    className="px-4 py-8 text-center text-gray-400"
                  >
                    No users found
                  </td>
                </tr>
              ) : (
                filtered.map((user) => {
                  const roleKey = user.groups[0] || "customer_user";
                  const roleLabel = ROLE_LABELS[roleKey] || roleKey;
                  const roleBadge =
                    ROLE_BADGE_COLORS[roleKey] || "bg-gray-100 text-gray-600";
                  return (
                    <tr
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                      className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {user.first_name || user.last_name
                          ? `${user.first_name} ${user.last_name}`.trim()
                          : user.username}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {user.email || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleBadge}`}
                        >
                          {roleLabel}
                        </span>
                      </td>
                      {isSuperAdmin && (
                        <td className="px-4 py-3 text-gray-500">
                          {user.customer_name || (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3 text-gray-500">
                        {user.regions.length === 0 ? (
                          <span className="text-gray-300">—</span>
                        ) : user.regions.length === 1 ? (
                          user.regions[0].name
                        ) : (
                          `${user.regions[0].name} +${user.regions.length - 1}`
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[user.status]}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[user.status]}`}
                          />
                          {user.status.charAt(0).toUpperCase() +
                            user.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
